/**
 * Tipos canônicos dos eventos WebSocket do frontend — espelha
 * `apps/engine/src/modules/websocket/websocket.types.ts`. Movido do web
 * para `@lume/shared` no B3 mobile (consumo cross-plataforma; mata os
 * espelhos manuais web+mobile).
 */
export const WS_EVENTS = {
    MESSAGE_RECEIVED: 'conversation.message.received',
    CONVERSATION_CREATED: 'conversation.created',
    CONVERSATION_STATUS_CHANGED: 'conversation.status.changed',
    // Fatia 6 PR-A
    MESSAGE_SENT: 'conversation.message.sent',
    MESSAGE_DELIVERY_UPDATED: 'conversation.message.delivery.updated',
    // Fatia 7 — sugestão IA inline.
    // Item 287 (FE-2) — sinal de geração em curso ("IA gerando…").
    AI_SUGGESTION_GENERATING: 'ai.suggestion.generating',
    AI_SUGGESTION_READY: 'ai.suggestion.ready',
    AI_SUGGESTION_ERROR: 'ai.suggestion.error',
    // PR-B Fatia 7 — ConflictAlert handler.
    AI_SUGGESTION_BLOCKED_BY_CONFLICT: 'ai.suggestion.blocked-by-conflict',
    // Fatia 8 — classification + summary + behavioral.
    LIVING_SUMMARY_UPDATED: 'living-summary.updated',
    QUALITY_SIGNALS_UPDATED: 'quality-signals.updated',
    LEVEL_4_DETECTED: 'level-4.detected',
    BEHAVIORAL_PATTERN_DETECTED: 'behavioral.pattern-detected',
    RISK_SCORE_UPDATED: 'risk-score.updated',
    // Fatia E — checklists + checklist-agent.
    CHECKLIST_UPDATED: 'checklist.updated',
    CHECKLIST_AGENT_PROPOSAL_MADE: 'checklist-agent.proposal-made',
    // Fatia F — lifecycle + sequences.
    CONVERSATION_LIFECYCLE_TRANSITIONED: 'conversation.lifecycle.transitioned',
    CONVERSATION_OUTCOME_SET: 'conversation.outcome.set',
    CONVERSATION_SEQUENCE_FLAG_RAISED: 'conversation_sequence.flag-raised',
    CONVERSATION_SEQUENCE_MEMBER_ADDED: 'conversation_sequence.member-added',
    // Fatia 11.2 — modo H_c refinements (auto-close + transition + conflict + timeout + F1 fix).
    OPERATION_MODE_CHANGED: 'conversation.operation_mode.changed',
    PLAYBOOK_TRANSITION_EXECUTED: 'ai.transition.executed',
    // S7 PR3 (S6 Condução Governada, Q-J6) — proposta `requires_confirmation`
    // criada (via reativa `transition-agent` OU promotor de jornada
    // `on_success_pointer`). Emitido pelo Engine em `ai-transition.service.ts`
    // (R15: `websocket.types.ts` — já existia lá, ausente aqui). S7 consome
    // para invalidar a superfície de confirmar/rejeitar.
    PLAYBOOK_TRANSITION_PROPOSED: 'ai.transition.proposed',
    CONDUCTOR_TIMEOUT_FALLBACK: 'ai.conducting.timeout',
    CONDUCTOR_CONFLICT_BLOCKED: 'ai.conducting.conflict-blocked',
    // Items 280+281 (T B4) — fluxo canonical de escalation consent modo H_c.
    AI_CONDUCTING_ESCALATION_SUGGESTED: 'ai.conducting.escalation-suggested',
    AI_CONDUCTING_ESCALATION_CONFIRMED: 'ai.conducting.escalation-confirmed',
    AI_CONDUCTING_ESCALATION_DECLINED_OR_TIMEOUT: 'ai.conducting.escalation-declined-or-timeout',
    // S4 Onda 4 (4b) — modo recepcionista: a escalação aconteceu e a IA SEGURA a
    // fila em H_c até o claim (escalationState awaiting_claim). Gated OFF.
    AI_CONDUCTING_QUEUE_HELD: 'ai.conducting.queue-held',
    // Sub-fatia 10.0 PR 3 (ADR-142) — contexto externo refrescado para Person.
    PERSON_CONTEXT_REFRESHED: 'person.context.refreshed',
    // Sprint A4 S1 (Item 308) — Admin integrações MCP.
    MCP_HANDSHAKE_COMPLETED: 'mcp.handshake-completed',
    INTEGRATIONS_FEATURE_TOGGLED: 'integrations.feature-toggled',
    // Brief 3 V2 Tela 1 — Supervisor Dashboard (S10).
    SUPERVISOR_DASHBOARD_SNAPSHOT_UPDATED: 'supervisor-dashboard.snapshot-updated',
    SUPERVISOR_DASHBOARD_WIDGET_PREFERENCE_CHANGED: 'supervisor-dashboard.widget-preference-changed',
    SUPERVISOR_DASHBOARD_PRIORITY_QUEUE_CHANGED: 'supervisor-dashboard.priority-queue-changed',
    // Brief 3 Tela 8 — Painel de Gestão de P1 (módulo p1 — websocket.types.ts).
    P1_DETECTED: 'p1.detected',
    P1_AUTO_ESCALATED: 'p1.auto-escalated',
    P1_VOLUME_CRITICAL: 'p1.volume-critical',
    P1_ACKNOWLEDGED: 'p1.acknowledged',
    P1_HANDLED: 'p1.handled',
    P1_RESOLVED: 'p1.resolved',
    P1_CLOSED: 'p1.closed',
    P1_SEVERITY_ESCALATED: 'p1.severity-escalated',
    P1_BYPASSED_OFFLINE_ATTENDANT: 'p1.bypassed-offline-attendant',
    // ADR-146 — prioridade operacional alterada por cron SLA (sem co-evento de mensagem).
    CONVERSATION_PRIORITY_CHANGED: 'conversation.priority-changed',
    // Item 368 F2 — proposta de condução H_c (ConductionProposal). Emitido ao
    // `user:{assignedToUserId}` da conversa. R15: engine websocket.types.ts.
    CONDUCTION_PROPOSAL_CREATED: 'conduction-proposal.created',
    // Brief 3 Tela 2 — Acompanhamento Silencioso (peer-coordination — websocket.types.ts).
    // T2-1 usa CONVERSATION_ATTENDANT_COMPOSING (dirigido) + reusa os eventos de
    // conversa (MESSAGE_RECEIVED/…); os demais entram no WS sync da T2-2 (coaching/
    // session-note/discrepancy). Strings confirmadas R15 no websocket.types do engine.
    CONVERSATION_ATTENDANT_COMPOSING: 'conversation.attendant-composing',
    // Frente engine-led (Condução Governada S7 — sinal de foco por-conversa,
    // 12/Jul). Peça 1: o operador sinaliza que ABRIU/FECHOU a Conversa X (open →
    // viewing:true; unmount/close → viewing:false). Emitido pelo Engine
    // DIRECIONADO a co-attendant observers (molde CONVERSATION_ATTENDANT_COMPOSING);
    // o estado server-side (TTL) alimenta o alerta de cliente-em-espera (Peça 3).
    CONVERSATION_ATTENDANT_VIEWING: 'conversation.attendant-viewing',
    COATTENDANCE_STARTED: 'coattendance.started',
    COATTENDANCE_ENDED: 'coattendance.ended',
    COATTENDANCE_ROLE_CHANGED: 'coattendance.role-changed',
    COACHING_MESSAGE_CREATED: 'coaching-message.created',
    SUPERVISOR_SESSION_NOTE_AUTO_SAVED: 'supervisor-session-note.auto-saved',
    COATTENDANCE_DISCREPANCY_DETECTED: 'coattendance.discrepancy-detected',
    // Mensageria interna (peer-coordination/direct-messages — websocket.types.ts).
    // Emitidos per-participant via emitToUser; o remetente também recebe o
    // próprio direct-message.created.
    DIRECT_THREAD_CREATED: 'peer-coordination.direct-thread.created',
    DIRECT_MESSAGE_CREATED: 'peer-coordination.direct-message.created',
    DIRECT_THREAD_READ: 'peer-coordination.direct-thread.read',
    // N2 (Modernização Mobile) — notificação in-app canônica. Emitida pelo
    // `InAppNotificationAdapter` do Engine (`notifications` module) para
    // `user:{userId}` com subject/body JÁ renderizados. É o chokepoint que a
    // camada in-app do mobile consome (`buildInAppNotification` de
    // `@lume/shared/push`) — NÃO os eventos de domínio crus. Antes só existia
    // no `websocket.types.ts` do Engine; agora entra no catálogo do frontend.
    NOTIFICATION_CREATED: 'notification.created',
};
//# sourceMappingURL=index.js.map